import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LoanService } from '../../services/loan/loan.service';
import { ToastService } from '../../services/general/toast.service';
import { SearchComponent } from '../../shared/search/search.component';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { ProgressLoaderComponent } from '../../components/loaders/progress-loader/progress-loader.component';
import { ExportButtonComponent } from '../../components/buttons/export-button/export-button.component';
import { FiltersComponent } from '../../shared/filter/filters/filters.component';
import { Loan, LoanAction, LoanQueryParams, CreateLoanRepaymentDto, LoanRepaymentDto } from '../../interfaces/responses/loan.interface';
import { CustomInputComponent } from '../../components/input/custom-input/custom-input.component';
import { CustomSelectInputComponent } from '../../components/input/custom-select-input/custom-select-input.component';
import { Validators } from '@angular/forms';
import { LoanStatusStylePipe, LoanStatusLabelPipe } from '../../pipes/loan-status.pipe';
import { PaginationInterface } from '../../interfaces/responses/pagination.interface';
import { debounceTime, distinctUntilChanged, interval, Subscription } from 'rxjs';
import { ExportService } from '../../services/general/export.service';
import { ImagesService } from '../../services/general/images.service';
import { ViewNewApplicationComponent } from '../../components/card/all-product/view-new-application/view-new-application.component';
import { ConfirmationDialogComponent } from '../../components/shared/confirmation-dialog/confirmation-dialog.component';
import { PermissionService } from '../../services/permission/permission.service';

@Component({
  selector: 'app-loan-applications',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SearchComponent,
    PaginationComponent,
    ProgressLoaderComponent,
    ExportButtonComponent,
    FiltersComponent,
    ViewNewApplicationComponent,
    ConfirmationDialogComponent,
    CustomInputComponent,
    CustomSelectInputComponent,
    LoanStatusStylePipe,
    LoanStatusLabelPipe,
  ],
  templateUrl: './loan-applications.component.html',
  styleUrl: './loan-applications.component.scss'
})
export class LoanApplicationsComponent implements OnInit, OnDestroy {
  private loanService = inject(LoanService);
  private _toast = inject(ToastService);
  private _fb = inject(FormBuilder);
  private _router = inject(Router);
  private exportService = inject(ExportService);
  permissionService = inject(PermissionService);
  _images = inject(ImagesService);

  activeLoanId: string | null = null;

  dropdownOptions = [
    { title: 'Manage', icon: this._images.visible },
  ];

  toggleActions(loanId: string) {
    this.activeLoanId = this.activeLoanId === loanId ? null : loanId;
  }

  loans: PaginationInterface<Loan[]> = {
    data: [],
    meta: {
      totalPages: 0,
      perPage: 10,
      page: 1,
      total: 0
    }
  };

  // Client-side filter state
  private allFetchedLoans: Loan[] = [];
  private clientFilteredLoans: Loan[] = [];
  private activeFilterValues: any = null;
  private readonly clientPerPage = 10;

  get isFilterActive(): boolean {
    return this.activeFilterValues !== null;
  }

  isLoading = false;
  viewMode: 'table' | 'grid' = 'table';
  searchQuery!: FormGroup;
  showModal = false;
  currentLoan: Loan | null = null;

  // Action dialog state
  showApproveDialog = false;
  showRejectDialog = false;
  showCounterDialog = false;
  showDisburseDialog = false;
  pendingLoan: Loan | null = null;

  // Repayment history
  loanRepayments: LoanRepaymentDto[] = [];
  isLoadingRepayments = false;
  activeModalTab: 'details' | 'repayments' = 'details';

  get totalRepaid(): number {
    return this.loanRepayments.reduce((sum, r) => sum + +r.amount, 0);
  }

  get repaymentBalance(): number {
    return +(this.currentLoan?.totalRepayment ?? 0) - this.totalRepaid;
  }

  // Repayment modal
  showRepayModal = false;
  isSubmittingRepay = false;
  repaymentTypes = [
    { label: 'Bank', value: 'BANK' },
    { label: 'Cash', value: 'CASH' },
    { label: 'Mobile Money', value: 'MOMO' },
  ];

  repayForm: FormGroup = this._fb.group({
    type: ['', Validators.required],
    transactionId: ['', Validators.required],
    amount: ['', [Validators.required, Validators.min(0.01)]],
    date: ['', Validators.required],
    comment: [''],
  });
  rejectionReason = '';
  counterForm = { comment: '', amount: null as number | null, tenure: null as number | null };

  loanQueryParams: LoanQueryParams = {
    paginate: true,
    perPage: 10,
    page: 1,
  };

  // Auto-refresh
  autoRefreshEnabled = false;
  private readonly autoRefreshIntervalMs = 60000;
  private autoRefreshSub: Subscription | null = null;

  resetFilters() {
    this.activeFilterValues = null;
    this.allFetchedLoans = [];
    this.clientFilteredLoans = [];
    const { paginate, perPage, keyword } = this.loanQueryParams;
    this.loanQueryParams = { paginate, perPage, page: 1, keyword };
  }

  ngOnInit() {
    this.searchQuery = this._fb.group({
      search: ['']
    });

    this.searchQuery.get('search')?.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((query: string) => {
        this.loanQueryParams.keyword = query.trim();
        this.loanQueryParams.page = 1;
        this.loadLoanApplications();
      });

    this.loadLoanApplications();
  }

  loadLoanApplications() {
    this.isLoading = true;
    this.loanService.getPaginatedProductLoans(this.loanQueryParams).subscribe({
      next: (res) => {
        this.loans = res;
this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching loan applications:', err);
        this._toast.error('Error loading loan applications');
        this.isLoading = false;
      }
    });
  }

  toggleAutoRefresh() {
    this.autoRefreshEnabled = !this.autoRefreshEnabled;
    if (this.autoRefreshEnabled) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  private startAutoRefresh() {
    this.stopAutoRefresh();
    this.autoRefreshSub = interval(this.autoRefreshIntervalMs).subscribe(() => {
      if (this.isLoading || this.isAnyDialogOpen() || document.hidden) return;
      this.refreshData();
    });
  }

  private stopAutoRefresh() {
    this.autoRefreshSub?.unsubscribe();
    this.autoRefreshSub = null;
  }

  private isAnyDialogOpen(): boolean {
    return this.showModal || this.showRepayModal || this.showApproveDialog ||
      this.showRejectDialog || this.showCounterDialog || this.showDisburseDialog;
  }

  private refreshData() {
    if (this.isFilterActive) {
      this.loanService.getPaginatedProductLoans({ paginate: false, keyword: this.loanQueryParams.keyword }).subscribe({
        next: (res) => {
          this.allFetchedLoans = res.data;
          this.applyClientFilters(this.loans.meta.page ?? 1);
        },
        error: () => {}
      });
    } else {
      this.loanService.getPaginatedProductLoans(this.loanQueryParams).subscribe({
        next: (res) => { this.loans = res; },
        error: () => {}
      });
    }
  }

  ngOnDestroy() {
    this.stopAutoRefresh();
  }

  onPageChange(page: number) {
    if (this.isFilterActive) {
      this.applyClientFilters(page);
    } else {
      this.loanQueryParams.page = page;
      this.loadLoanApplications();
    }
  }

  filterApplications(filterData: FormGroup) {
    const v = filterData.value;
    const hasAnyFilter = Object.values(v).some(val => val !== null && val !== undefined && val !== '');

    if (!hasAnyFilter) {
      this.resetFilters();
      this.loadLoanApplications();
      return;
    }

    this.activeFilterValues = v;
    this.isLoading = true;

    this.loanService.getPaginatedProductLoans({ paginate: false, keyword: this.loanQueryParams.keyword }).subscribe({
      next: (res) => {
        this.allFetchedLoans = res.data;
        this.applyClientFilters(1);
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  private applyClientFilters(page: number) {
    const f = this.activeFilterValues;
    if (!f) return;

    this.clientFilteredLoans = this.allFetchedLoans.filter(loan => {
      if (f.status && loan.status !== f.status) return false;
      if (f.institutionId) {
        const institutionName = typeof loan.institution === 'string' ? loan.institution : loan.institution?.name;
        if ((institutionName || '').trim().toLowerCase() !== (f.institutionName || '').trim().toLowerCase()) return false;
      }
      if (f.startDate) {
        const loanDate = new Date(loan.created_at || loan.createdAt!);
        if (loanDate < new Date(f.startDate)) return false;
      }
      if (f.endDate) {
        const loanDate = new Date(loan.created_at || loan.createdAt!);
        if (loanDate > new Date(f.endDate + 'T23:59:59')) return false;
      }
      const amountValue = Number(loan.amount ?? loan.principal_amount ?? 0);
      if (f.minAmount != null && f.minAmount !== '' && amountValue < +f.minAmount) return false;
      if (f.maxAmount != null && f.maxAmount !== '' && amountValue > +f.maxAmount) return false;
      return true;
    });

    const total = this.clientFilteredLoans.length;
    const totalPages = Math.ceil(total / this.clientPerPage) || 1;
    const start = (page - 1) * this.clientPerPage;

    this.loans = {
      data: this.clientFilteredLoans.slice(start, start + this.clientPerPage),
      meta: { page, perPage: this.clientPerPage, total, totalPages }
    };
  }

  copiedRef: string | null = null;

  copyReference(ref: string, event: Event): void {
    event.stopPropagation();
    navigator.clipboard.writeText(ref).then(() => {
      this.copiedRef = ref;
      setTimeout(() => { this.copiedRef = null; }, 1500);
    });
  }

  viewApplication(loan: Loan) {
    this.currentLoan = loan;
    this.loanRepayments = [];
    this.activeModalTab = 'details';
    this.showModal = true;
    if (loan.id) this.fetchLoanRepayments(loan.id);
  }

  fetchLoanRepayments(loanId: string) {
    this.isLoadingRepayments = true;
    this.loanService.getRepayments({ loanId, paginate: false }).subscribe({
      next: (res: any) => {
        this.loanRepayments = Array.isArray(res) ? res : (res.data ?? []);
        this.isLoadingRepayments = false;
      },
      error: () => { this.isLoadingRepayments = false; }
    });
  }

  closeModal() {
    this.showModal = false;
    this.currentLoan = null;
    this.loanRepayments = [];
  }


  openRepayModal(): void {
    this.repayForm.reset();
    this.repayForm.markAsPristine();
    this.repayForm.markAsUntouched();
    this.showRepayModal = true;
  }

  closeRepayModal(): void {
    this.showRepayModal = false;
  }

  submitRepayment(): void {
    if (this.repayForm.invalid) {
      this.repayForm.markAllAsTouched();
      return;
    }
    const loanReference = this.currentLoan?.reference;
    const loanId = this.currentLoan?.id;
    if (!loanReference || !loanId) return;
    this.isSubmittingRepay = true;
    const { type, transactionId, amount, date, comment } = this.repayForm.value;
    this.loanService.createRepayment({ loanId: loanReference, type, transactionId, amount: +amount, date, comment }).subscribe({
      next: () => {
        this._toast.success('Repayment recorded successfully');
        this.closeRepayModal();
        this.isSubmittingRepay = false;
        if (this.currentLoan?.id) {
          this.fetchLoanRepayments(this.currentLoan.id);
          this.loanService.getLoanById(this.currentLoan.id).subscribe({
            next: (loan) => { this.currentLoan = loan; },
            error: () => {}
          });
        }
      },
      error: (err) => {
        this._toast.error(err?.message || 'Failed to record repayment');
        this.isSubmittingRepay = false;
      }
    });
  }


  handleAction(loan: Loan, action: 'APPROVE' | 'REJECT' | 'COUNTER' | 'DISBURSE') {
    this.pendingLoan = loan;
    if (action === 'APPROVE') {
      this.showApproveDialog = true;
    } else if (action === 'COUNTER') {
      this.counterForm = { comment: '', amount: null, tenure: null };
      this.showCounterDialog = true;
    } else if (action === 'DISBURSE') {
      this.showDisburseDialog = true;
    } else {
      this.rejectionReason = '';
      this.showRejectDialog = true;
    }
  }

  confirmApprove() {
    if (!this.pendingLoan) return;
    this.showApproveDialog = false;
    this.isLoading = true;
    this.loanService.loanAction(this.pendingLoan.id!, { action: LoanAction.APPROVE }).subscribe({
      next: (updatedLoan) => {
        this._toast.success('Loan approved successfully');
        this.currentLoan = updatedLoan;
        this.pendingLoan = null;
        this.loadLoanApplications();
      },
      error: (err) => {
        console.error('Error approving loan:', err);
        this._toast.error(err?.message || 'Failed to approve loan');
        this.pendingLoan = null;
        this.isLoading = false;
      }
    });
  }

  cancelApprove() {
    this.showApproveDialog = false;
    this.pendingLoan = null;
  }

  confirmReject() {
    if (!this.pendingLoan || !this.rejectionReason.trim()) return;
    this.showRejectDialog = false;
    this.isLoading = true;
    this.loanService.loanAction(this.pendingLoan.id!, { action: LoanAction.REJECT, comment: this.rejectionReason.trim() }).subscribe({
      next: (updatedLoan) => {
        this._toast.success('Loan rejected successfully');
        this.currentLoan = updatedLoan;
        this.pendingLoan = null;
        this.rejectionReason = '';
        this.loadLoanApplications();
      },
      error: (err) => {
        console.error('Error rejecting loan:', err);
        this._toast.error(err?.message || 'Failed to reject loan');
        this.pendingLoan = null;
        this.isLoading = false;
      }
    });
  }

  cancelReject() {
    this.showRejectDialog = false;
    this.pendingLoan = null;
    this.rejectionReason = '';
  }

  confirmCounter() {
    if (!this.pendingLoan || !this.counterForm.amount || !this.counterForm.tenure) return;
    this.showCounterDialog = false;
    this.isLoading = true;
    this.loanService.loanAction(this.pendingLoan.id!, {
      action: LoanAction.COUNTER,
      comment: this.counterForm.comment.trim() || undefined,
      amount: this.counterForm.amount,
      tenure: this.counterForm.tenure,
    }).subscribe({
      next: (updatedLoan) => {
        this._toast.success('Counter offer submitted successfully');
        this.currentLoan = updatedLoan;
        this.pendingLoan = null;
        this.counterForm = { comment: '', amount: null, tenure: null };
        this.loadLoanApplications();
      },
      error: (err) => {
        console.error('Error submitting counter:', err);
        this._toast.error(err?.message || 'Failed to submit counter offer');
        this.pendingLoan = null;
        this.isLoading = false;
      }
    });
  }

  cancelCounter() {
    this.showCounterDialog = false;
    this.pendingLoan = null;
    this.counterForm = { comment: '', amount: null, tenure: null };
  }

  confirmDisburse() {
    if (!this.pendingLoan) return;
    this.showDisburseDialog = false;
    this.isLoading = true;
    this.loanService.loanAction(this.pendingLoan.id!, { action: LoanAction.DISBURSE }).subscribe({
      next: (updatedLoan) => {
        this._toast.success('Loan disbursed successfully');
        this.currentLoan = updatedLoan;
        this.pendingLoan = null;
        this.loadLoanApplications();
      },
      error: (err) => {
        console.error('Error disbursing loan:', err);
        this._toast.error(err?.message || 'Failed to disburse loan');
        this.pendingLoan = null;
        this.isLoading = false;
      }
    });
  }

  cancelDisburse() {
    this.showDisburseDialog = false;
    this.pendingLoan = null;
  }

  /**
   * Handle export based on type
   */
  handleExport(type: 'Excel' | 'PDF' | 'CSV'): void {
    if (!this.loans.data || this.loans.data.length === 0) {
      this._toast.warning('No data available to export');
      return;
    }

    const exportData = this.loans.data.map(loan => ({
      'Entry Date': (loan.created_at || loan.createdAt) ? new Date(loan.created_at || loan.createdAt!).toLocaleDateString() : '---',
      'Ref ID': loan.id?.substring(0, 8).toUpperCase() || '---',
      'Applicant': (loan.member?.first_name || loan.user?.firstName || '') + ' ' + (loan.member?.last_name || loan.user?.lastName || ''),
      'Institution': (typeof loan.institution === 'string' ? loan.institution : loan.institution?.name) || '---',
      'Ghana Card': loan.userGhanaCard || loan.user?.ghanaCardNumber || '---',
      'Principal Amount': loan.amount || loan.principal_amount || '0',
      'Interest Rate': loan.interestRate != null ? `${loan.interestRate}%` : '---',
      'Interest Amount': loan.interest || '0',
      'Product': loan.product?.name || '---',
      'Status': loan.status?.replace('_', ' ') || 'UNKNOWN',
      'Tenure': loan.tenure ? `${loan.tenure} ${loan.product?.tenureType === 'DAILY' ? 'day' : 'month'}${loan.tenure !== 1 ? 's' : ''}` : '---'
    }));

    const filename = `loan_applications_${new Date().toISOString().split('T')[0]}`;

    switch (type) {
      case 'Excel':
        this.exportService.exportToExcel(exportData, filename);
        this._toast.success('Loan applications exported to Excel successfully');
        break;
      case 'PDF':
        this.exportService.exportToPDF(exportData, filename, 'Loan Applications');
        this._toast.success('Loan applications exported to PDF successfully');
        break;
      case 'CSV':
        this.exportService.exportToCSV(exportData, filename);
        this._toast.success('Loan applications exported to CSV successfully');
        break;
    }
  }
}

